import type { NavParams, Page } from '@types';

export interface AppRoute {
    page: Page;
    params: NavParams;
}

const PAGE_PATHS: Partial<Record<Page, string>> = {
    organizationDashboard: 'dashboard',
    knowledgeHub: 'knowledge',
    pillarsDashboard: 'pillars',
    dashboard: 'pillar',
    suiteDashboard: 'suite',
    gsocDashboard: 'threat-analysis',
    gtg_dashboard: 'threat-groups',
    srm_command_center: 'project',
    srmFrameworkMonitor: 'project-monitor',
    global_risk_register: 'risk-register',
    resiliencyHub: 'resiliency',
    executiveReport: 'report',
    analysisDisplay: 'analysis',
    reportLibrary: 'report-library',
    consultantReport: 'on-demand-report',
    deliveredReport: 'delivered-report',
    serviceRequests: 'service-requests',
    myAccount: 'account',
    countryIntel: 'country',
    agencyDashboard: 'agency',
    clientDetail: 'agency/client',
    superAdmin: 'management',
    subscriptionManagement: 'subscription',
    subscriptionSetup: 'subscription/setup',
    upgradeBenefits: 'upgrade',
    training: 'training',
    instructionManual: 'manual',
    methodology: 'methodology',
    pending_clearance: 'pending-clearance'
};

const PATH_PAGES = new Map(Object.entries(PAGE_PATHS).map(([page, path]) => [path, page as Page]));
const VALID_PAGES = new Set<Page>([
    'marketing', 'branding', 'auth', 'subscription', 'knowledgeHub', 'pillarsDashboard',
    'globalCommandCenter', 'executiveReport', 'CountryAnalysisResults', 'OSINTDossierResults',
    'analysisDisplay', 'industryHub', 'customIndustryBuilder', 'dashboard', 'suiteDashboard',
    'gsocDashboard', 'gtg_dashboard', 'instructionManual', 'training', 'help',
    'superAdmin', 'superAdminDashboard', 'srm_command_center', 'integrated_threat_analysis', 'gsoc',
    'resiliency_hub', 'resiliencyHub', 'knowledge_hub', 'pillars', 'methodology',
    'current_framework_dashboard', 'terms-of-service', 'privacy-policy', 'data-and-ai-policy',
    'legal-disclaimer', 'subscription-agreement', 'refund-policy', 'cancellation-policy',
    'subscriptionSetup', 'subscriptionManagement', 'contact',
    'organizationDashboard', 'rgs', 'srm_monitor', 'pending_clearance', 'srmFrameworkMonitor',
    'countryIntel', 'agencyDashboard', 'clientDetail', 'upgradeBenefits', 'global_risk_register',
    'GovernanceStructure', 'devSprint', 'reportLibrary', 'consultantReport', 'deliveredReport', 'serviceRequests', 'myAccount'
]);
const PUBLIC_PATHS: Record<string, Page> = {
    '': 'auth',
    auth: 'auth',
    login: 'auth',
    contact: 'contact',
    'terms-of-service': 'terms-of-service',
    'privacy-policy': 'privacy-policy',
    'data-and-ai-policy': 'data-and-ai-policy',
    'legal-disclaimer': 'legal-disclaimer',
    'subscription-agreement': 'subscription-agreement',
    'refund-policy': 'refund-policy',
    'cancellation-policy': 'cancellation-policy',
    methodology: 'methodology'
};

const PARAM_KEYS = [
    'industry', 'suiteName', 'country', 'state', 'workflowId', 'countryId',
    'frameworkComponentId', 'view', 'workflow', 'purpose', 'clientId',
    'incidentType', 'severityLevel', 'activeClientId', 'projectId', 'moduleId',
    'targetModuleId', 'activeWorkflowId', 'prefilledCountry', 'decommissionProjectId',
    'suiteId', 'iso3', 'reportId', 'reportDate', 'frameworkId', 'autoRunReport',
    'preselectedCountry', 'activeTab', 'preselectedPlan', 'auditId', 'promptId', 'serviceRequestId',
    'serviceCode', 'serviceRequestDraftId', 'frameworkRequestId'
] as const;
const ARRAY_PARAM_KEYS = ['activeClientCountries', 'clientCountries'] as const;

function value(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function routePath(page: Page, params: NavParams): string {
    if (page === 'srm_command_center' && value(params.projectId))
        return `project/${encodeURIComponent(value(params.projectId))}`;
    if (page === 'executiveReport' && value(params.reportId))
        return `report/${encodeURIComponent(value(params.reportId))}`;
    if (page === 'countryIntel' && value(params.countryId || params.iso3))
        return `country/${encodeURIComponent(value(params.countryId || params.iso3))}`;
    if (page === 'clientDetail' && value(params.clientId))
        return `agency/client/${encodeURIComponent(value(params.clientId))}`;
    return PAGE_PATHS[page] || `page/${encodeURIComponent(page)}`;
}

export function serializeHashRoute(page: Page, params: NavParams = {}): string {
    const query = new URLSearchParams();
    const path = routePath(page, params);
    for (let index = 0; index < PARAM_KEYS.length; index++) {
        const key = PARAM_KEYS[index];
        const item = key === 'reportDate' && page === 'CountryAnalysisResults' && params.isDailyMicro === true
            ? params.reportDate || params.reportData?.reportDate
            : (params as Record<string, unknown>)[key];
        if ((key === 'projectId' && path.startsWith('project/')) ||
            (key === 'reportId' && path.startsWith('report/')) ||
            (key === 'clientId' && path.startsWith('agency/client/')) ||
            ((key === 'countryId' || key === 'iso3') && path.startsWith('country/')))
            continue;
        if (typeof item === 'string' && item.trim())
            query.set(key, item.trim());
    }
    if (params.isDailyMicro === true)
        query.set('isDailyMicro', '1');
    for (let index = 0; index < ARRAY_PARAM_KEYS.length; index++) {
        const key = ARRAY_PARAM_KEYS[index];
        const items = (params as Record<string, unknown>)[key];
        if (!Array.isArray(items))
            continue;
        for (let itemIndex = 0; itemIndex < items.length; itemIndex++)
            if (typeof items[itemIndex] === 'string' && items[itemIndex].trim())
                query.append(key, items[itemIndex].trim());
    }
    const suffix = query.toString();
    return `#/${path}${suffix ? `?${suffix}` : ''}`;
}

export function parseHashRoute(hash: string): AppRoute | null {
    if (!hash.startsWith('#/'))
        return null;
    const raw = hash.slice(2);
    const queryIndex = raw.indexOf('?');
    const path = (queryIndex >= 0 ? raw.slice(0, queryIndex) : raw).replace(/^\/+|\/+$/g, '');
    const query = new URLSearchParams(queryIndex >= 0 ? raw.slice(queryIndex + 1) : '');
    let page = PATH_PAGES.get(path);
    const params: Record<string, unknown> = {};

    if (path.startsWith('project/')) {
        page = 'srm_command_center';
        params.projectId = decodeURIComponent(path.slice('project/'.length));
    } else if (path.startsWith('report/')) {
        page = 'executiveReport';
        params.reportId = decodeURIComponent(path.slice('report/'.length));
    } else if (path.startsWith('country/')) {
        page = 'countryIntel';
        params.countryId = decodeURIComponent(path.slice('country/'.length));
    } else if (path.startsWith('agency/client/')) {
        page = 'clientDetail';
        params.clientId = decodeURIComponent(path.slice('agency/client/'.length));
    } else if (path.startsWith('page/')) {
        const candidate = decodeURIComponent(path.slice('page/'.length)) as Page;
        page = VALID_PAGES.has(candidate) ? candidate : undefined;
    }
    if (!page)
        return null;

    for (let index = 0; index < PARAM_KEYS.length; index++) {
        const key = PARAM_KEYS[index];
        const item = query.get(key);
        if (item)
            params[key] = item;
    }
    if (query.get('isDailyMicro') === '1')
        params.isDailyMicro = true;
    for (let index = 0; index < ARRAY_PARAM_KEYS.length; index++) {
        const key = ARRAY_PARAM_KEYS[index];
        const items = query.getAll(key).filter(Boolean);
        if (items.length)
            params[key] = items;
    }
    return { page, params: params as NavParams };
}

export function routeFromLocation(location: Pick<Location, 'hash' | 'pathname'>): AppRoute {
    const hashRoute = parseHashRoute(location.hash);
    if (hashRoute)
        return hashRoute;
    const path = location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
    return { page: PUBLIC_PATHS[path] || 'auth', params: {} };
}

export function isPublicPathPage(page: Page): boolean {
    return Object.values(PUBLIC_PATHS).includes(page);
}
